import React, { useEffect } from 'react';

import { FormComponentProps } from 'antd/lib/form/Form';
import { FormattedMessage } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import { Col, Form, Input, Row, S } from '@shared/components';
import { intl } from '@shared/localization';
import { unitInfoRequest, unitInfoSel } from '@unit';

import styles from '../ActivityDetails/ActivityDetails.less';
import AddressState from './AddressState';

const FormItem = Form.Item;

interface ActivityLocationProps {
  isDisabled: boolean;
  form: FormComponentProps['form'];
  sectionTitle: string | React.ReactElement;
}

const ActivityLocation: React.FC<ActivityLocationProps> = ({
  form,
  sectionTitle,
  isDisabled,
}) => {
  const { getFieldDecorator } = form;
  const dispatch = useDispatch();
  const { primaryAddress: { zip5: unitZipCode = undefined } = {} } =
    useSelector(unitInfoSel);

  useEffect(() => {
    if (unitZipCode === undefined) {
      dispatch(unitInfoRequest());
    }
  }, [dispatch, unitZipCode]);

  return (
    <Row type="flex" justify="space-between">
      <Col span={24}>
        <React.Fragment>
          <S size="4" bold colored colon>
            {sectionTitle || (
              <FormattedMessage id="progress.ActivityLocation.title" />
            )}
          </S>
          <br />
          <FormItem colon={false}>
            {getFieldDecorator('location', {
              rules: [
                {
                  max: 255,
                  message: intl.formatMessage(
                    {
                      id: 'shared.maxCharacters',
                    },
                    { max: 255 },
                  ),
                },
              ],
            })(
              <Input
                placeholder={intl.formatMessage({
                  id: 'progress.ActivityLocation.field.location',
                })}
                size="large"
                rounded
                floatingLabel
                disabled={isDisabled}
              />,
            )}
          </FormItem>
          <FormItem colon={false}>
            {getFieldDecorator('addressLine1', {
              rules: [
                {
                  pattern: /^[. a-zA-Z0-9_-]*$/,
                  message: intl.formatMessage({
                    id: 'shared.address.address1.errors.specialCharsValidation',
                  }),
                },
                {
                  max: 255,
                  message: intl.formatMessage(
                    {
                      id: 'shared.maxCharacters',
                    },
                    { max: 255 },
                  ),
                },
              ],
            })(
              <Input
                size="large"
                className={styles.input}
                placeholder={intl.formatMessage({
                  id: 'shared.address.address1.placeholder',
                })}
                rounded
                floatingLabel
                disabled={isDisabled}
              />,
            )}
          </FormItem>
          <FormItem colon={false}>
            {getFieldDecorator('addressLine2', {
              rules: [
                {
                  pattern: /^[ a-zA-Z0-9_-]*$/,
                  message: intl.formatMessage({
                    id: 'shared.address.address1.errors.specialCharsValidation',
                  }),
                },
                {
                  max: 255,
                  message: intl.formatMessage(
                    {
                      id: 'shared.maxCharacters',
                    },
                    { max: 255 },
                  ),
                },
              ],
            })(
              <Input
                size="large"
                className={styles.input}
                placeholder={intl.formatMessage({
                  id: 'shared.address.address2.placeholder',
                })}
                rounded
                floatingLabel
                disabled={isDisabled}
              />,
            )}
          </FormItem>
          <FormItem colon={false}>
            {getFieldDecorator('city', {
              rules: [
                {
                  max: 50,
                  message: intl.formatMessage(
                    {
                      id: 'shared.maxCharacters',
                    },
                    { max: 50 },
                  ),
                },
              ],
            })(
              <Input
                size="large"
                className={styles.input}
                placeholder={intl.formatMessage({
                  id: 'shared.address.city.placeholder',
                })}
                rounded
                floatingLabel
                disabled={isDisabled}
              />,
            )}
          </FormItem>
          <AddressState
            floatingLabel
            rounded
            form={form}
            disabled={isDisabled}
          />
          <FormItem colon={false}>
            {getFieldDecorator('zip5', {
              rules: [
                {
                  pattern: /^(?:[0-9]{5})?$/,
                  message: intl.formatMessage({
                    id: 'progress.ActivityLocation.field.zipCodeError',
                  }),
                },
              ],
            })(
              <Input
                size="large"
                className={styles.input}
                placeholder={intl.formatMessage({
                  id: 'shared.address.zipCode.label',
                })}
                rounded
                floatingLabel
                disabled={isDisabled}
              />,
            )}
          </FormItem>
          <br />
        </React.Fragment>
      </Col>
    </Row>
  );
};

export default ActivityLocation;
